import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import * as moment from 'moment-timezone';
import {ChallengeAlert, ChallengeReward, ChallengeSalvage} from 'src/app/core/model/project';
import {
  DeleteAlertAction,
  DeleteRewardAction,
  DeleteSalvageAction,
  GetChallengeBuilderProjectAction,
  StopChallengeAction,
  UpdateAlertAction,
} from 'src/app/core/store/challenge-builder/challenge-builder.action';
import {selectChallengeBuilderProject} from 'src/app/core/store/challenge-builder/challenge-builder.selector';
import {MessageService} from 'src/app/services/message.service';
import {TimezoneService} from 'src/app/services/timezone.service';
import {OphBackButtonComponent} from 'src/app/shared/design/oph-back-button/oph-back-button.component';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {ChallengeWarningDialogComponent} from 'src/app/shared/projects/challenges/challenge-warning-dialog/challenge-warning-dialog.component';
import {OphSlideToggleComponent} from 'src/app/shared/design/oph-slide-toggle/oph-slide-toggle.component';
import {ListViewsModule} from 'src/app/shared/list-views/list-views.module';
import {TasksService} from 'src/app/shared/services/tasks.service';
import {getTargetDisplayString} from 'src/app/shared/utils/challenge-target-display.util';
import {CbAlertDialogComponent} from '../shared/cb-alert-dialog/cb-alert-dialog.component';
import {CbEditButtonComponent} from '../shared/cb-edit-button.component';
import {CbLaunchChallengeDialogComponent} from '../shared/cb-launch-challenge-dialog/cb-launch-challenge-dialog.component';
import {CbRewardDialogComponent} from '../shared/cb-reward-dialog/cb-reward-dialog.component';
import {CbSalvageDialogComponent} from '../shared/cb-salvage-dialog/cb-salvage-dialog.component';
import {CbScheduleChipComponent, ScheduleChipType} from '../shared/cb-schedule-chip.component';
import {CbTaskIconButtonComponent} from '../shared/cb-task-icon-button/cb-task-icon-button.component';
import {MobileService} from 'src/app/core/page/mobile.service';

interface TaskCheckIn {
  taskName: string;
  scheduleType: ScheduleChipType;
}

interface DaySchedule {
  dayLabel: string;
  tasks: TaskCheckIn[];
}

interface TaskMetricRow {
  showTaskName: boolean;
  taskName: string;
  taskId: string;
  taskIcon: string;
  metricName: string;
  metricType: string;
  metricTypeOption: string;
  targetValue: number | null;
  targetSentence: string;
}

@Component({
  selector: 'cb-overview',
  standalone: true,
  imports: [
    OphButtonModule,
    MatIconModule,
    ListViewsModule,
    CbEditButtonComponent,
    CbScheduleChipComponent,
    OphSlideToggleComponent,
    CbTaskIconButtonComponent,
    OphBackButtonComponent,
  ],
  templateUrl: './cb-overview.component.html',
  styleUrl: './cb-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CbOverviewComponent {
  private readonly mobileService = inject(MobileService);
  private store$ = inject(Store);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private dialog = inject(MatDialog);
  private timezoneService = inject(TimezoneService);
  private tasksService = inject(TasksService);

  cbProject = this.store$.selectSignal(selectChallengeBuilderProject);
  itemMenuOptions = ['edit', 'delete'];
  stopChallengeLoading = signal(false);
  taskIcons = signal<Record<string, string>>({});

  selectedGoal = computed(() => {
    const goals = this.cbProject()?.challengeBuilder?.goals;
    return goals?.find(g => g.selected)?.name || '';
  });

  selectedObjectives = computed(() => {
    const objectives = this.cbProject()?.challengeBuilder?.objectives || [];
    return objectives.filter(o => o.selected);
  });

  isActive = computed(() => {
    const id = this.cbProject()?._id;
    if (!id) return false;
    return this.cbProject()?.status === 'active';
  });

  taskMetricRows = computed<TaskMetricRow[]>(() => {
    const objectives = this.cbProject()?.challengeBuilder?.objectives || [];
    const tasks = objectives.flatMap(obj => obj.tasks || []).filter(t => t.selected);
    const icons = this.taskIcons();
    const rows: TaskMetricRow[] = [];

    for (const task of tasks) {
      const metrics = task.metrics || [];
      const taskId = task._id || '';
      const taskIcon =
        icons[taskId] ||
        task.icon ||
        (task.iconSuggestion ? this.tasksService.findIconByKeyword(task.iconSuggestion) : '') ||
        '';

      metrics.forEach((metric, index) => {
        const target = metric.target;
        rows.push({
          showTaskName: index === 0,
          taskName: task.name,
          taskId,
          taskIcon,
          metricName: metric.name,
          metricType: metric.type,
          metricTypeOption: metric.typeOption || '',
          targetValue: target?.targetValue ?? null,
          targetSentence: getTargetDisplayString(metric),
        });
      });
    }

    return rows;
  });

  onTaskIconChange(taskId: string, icon: string): void {
    this.taskIcons.update(icons => ({...icons, [taskId]: icon}));
  }

  onBack(): void {
    const projectId = this.cbProject()?._id;
    if (!projectId || this.cbProject()?.status === 'inactive') {
      this.router.navigate(['/projects']);
    } else {
      this.router.navigate(['/projects', projectId], {
        state: {fromChallengeBuilderBack: true},
      });
    }
  }

  onStartChallenge(): void {
    const cbProject = this.cbProject();
    if (!cbProject) return;

    this.dialog.open(CbLaunchChallengeDialogComponent, {
      backdropClass: 'oph-backdrop',
      panelClass: 'cb-launch-challenge-dialog-panel',
      width: '100%',
      maxWidth: '700px',
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      data: {cbProject},
    });
  }

  onStopChallenge(): void {
    const projectId = this.cbProject()?._id;
    if (!projectId) return;
    this.dialog.open(ChallengeWarningDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      backdropClass: 'oph-backdrop',
      panelClass: 'challenge-warning-dialog-panel',
      data: {
        type: 'challenge',
        action: 'stop',
        onConfirm: ({onSuccess, onFailure}) => {
          this.stopChallengeLoading.set(true);
          this.store$.dispatch(
            new StopChallengeAction({
              projectId,
              onSuccess: () => {
                this.stopChallengeLoading.set(false);
                this.store$.dispatch(new GetChallengeBuilderProjectAction({projectId}));
                onSuccess();
              },
              onFailure: () => {
                this.stopChallengeLoading.set(false);
                onFailure(new Error('Error stopping challenge'));
              },
            })
          );
        },
      },
    });
  }

  onEditChallenge(): void {
    const projectId = this.cbProject()?._id;
    if (projectId) {
      this.router.navigate(['projects', 'challenge-builder', projectId, 'goal']);
    }
  }

  daySchedules = computed<DaySchedule[]>(() => {
    const objectives = this.cbProject()?.challengeBuilder?.objectives || [];
    const tasks = objectives.flatMap(obj => obj.tasks || []).filter(t => t.selected);
    const cycleLength = this.cbProject()?.challengeBuilder.cycleLength || 0;

    if (cycleLength === 0 || tasks.length === 0) {
      return [];
    }

    const days: DaySchedule[] = [];

    for (let dayIndex = 0; dayIndex < cycleLength; dayIndex++) {
      const dayTasks: TaskCheckIn[] = [];

      for (const task of tasks) {
        const schedule = task.schedule || [];
        const checkInValue = schedule[dayIndex] || null;

        dayTasks.push({
          taskName: task.name,
          scheduleType: checkInValue as ScheduleChipType,
        });
      }

      days.push({
        dayLabel: `Day ${dayIndex + 1}`,
        tasks: dayTasks,
      });
    }

    return days;
  });

  onEditSchedule(): void {
    const projectId = this.cbProject()?._id;
    if (projectId) {
      this.router.navigate(['projects', 'challenge-builder', projectId, 'schedule']);
    }
  }

  openSalvageDialog(salvage?: ChallengeSalvage): void {
    this.dialog.open(CbSalvageDialogComponent, {
      backdropClass: 'oph-backdrop',
      panelClass: 'cb-salvage-dialog-panel',
      width: '100%',
      maxWidth: '438px',
      height: '100%',
      maxHeight: '585px',
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      data: {salvage, targets: this.cbProject()?.challengeBuilder?.targets || []},
    });
  }

  onAddReward(): void {
    this.dialog.open(CbRewardDialogComponent, {
      backdropClass: 'oph-backdrop',
      panelClass: 'cb-reward-dialog-panel',
      width: '100%',
      maxWidth: '470px',
      height: '100%',
      maxHeight: '600px',
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
    });
  }

  alerts = computed<ChallengeAlert[]>(() => {
    return this.cbProject()?.challengeBuilder?.alerts || [];
  });

  formatAlertTime(time: string): string {
    if (!time) return '';
    return moment(time).tz(this.timezoneService.getTimeZone()).format('h:mm A');
  }

  onAddAlert(): void {
    this.dialog.open(CbAlertDialogComponent, {
      backdropClass: 'oph-backdrop',
      panelClass: 'cb-alert-dialog-panel',
      width: '100%',
      maxWidth: '470px',
      height: '100%',
      maxHeight: '600px',
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
    });
  }

  onEditAlert(alert: ChallengeAlert): void {
    this.dialog.open(CbAlertDialogComponent, {
      backdropClass: 'oph-backdrop',
      panelClass: 'cb-alert-dialog-panel',
      width: '100%',
      maxWidth: '470px',
      height: '100%',
      maxHeight: '600px',
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      data: {alert},
    });
  }

  onToggleAlert(alert: ChallengeAlert, active: boolean): void {
    const projectId = this.cbProject()?._id;
    if (!projectId || !alert._id) return;
    const updatedAlert = {...alert, active};

    this.store$.dispatch(
      new UpdateAlertAction({
        projectId,
        alert: updatedAlert,
        onFailure: error => this.messageService.add('Error updating alert', 'error'),
      })
    );
  }

  onDeleteAlert(alert: ChallengeAlert): void {
    const projectId = this.cbProject()?._id;
    if (!projectId || !alert._id) return;

    this.store$.dispatch(
      new DeleteAlertAction({
        projectId,
        alertId: alert._id,
      })
    );
  }

  onSalvageMenu(action: string, salvage: ChallengeSalvage): void {
    if (action === 'edit') {
      this.openSalvageDialog(salvage);
    } else if (action === 'delete') {
      this.onDeleteSalvage(salvage._id);
    }
  }

  onDeleteSalvage(salvageId: string): void {
    const projectId = this.cbProject()?._id;
    if (!projectId) return;

    this.store$.dispatch(
      new DeleteSalvageAction({
        projectId,
        salvageId,
      })
    );
  }

  onRewardMenu(action: string, reward: ChallengeReward): void {
    if (action === 'edit') {
      this.onEditReward(reward);
    } else if (action === 'delete' && reward._id) {
      this.onDeleteReward(reward._id);
    }
  }

  onEditReward(reward: ChallengeReward): void {
    this.dialog.open(CbRewardDialogComponent, {
      backdropClass: 'oph-backdrop',
      panelClass: 'cb-reward-dialog-panel',
      width: '100%',
      maxWidth: '470px',
      height: '100%',
      maxHeight: '600px',
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      data: {reward},
    });
  }

  onDeleteReward(rewardId: string): void {
    const projectId = this.cbProject()?._id;
    if (!projectId) return;

    this.store$.dispatch(
      new DeleteRewardAction({
        projectId,
        rewardId,
      })
    );
  }

  onAlertMenu(action: string, alert: ChallengeAlert): void {
    if (action === 'edit') {
      this.onEditAlert(alert);
    } else if (action === 'delete') {
      this.onDeleteAlert(alert);
    }
  }
}
